import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, Save, Check, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const INDUSTRIES = [
  "Coach / Consultant",
  "Business Strategist",
  "Fitness / Wellness",
  "Creative / Designer",
  "Speaker / Author",
  "E-commerce / Product",
  "Food / Hospitality",
  "Beauty / Fashion",
  "Finance / Legal",
  "Technology",
  "Other",
];

const STYLES = [
  { value: "Professional & Corporate", desc: "Polished, sharp, business-first." },
  { value: "Warm & Approachable", desc: "Friendly, soft, inviting." },
  { value: "Bold & Energetic", desc: "High contrast, confident, dynamic." },
  { value: "Creative & Artistic", desc: "Editorial, unconventional, expressive." },
  { value: "Luxury & Premium", desc: "Refined, minimal, aspirational." },
  { value: "Casual & Relaxed", desc: "Easy-going, candid, lifestyle." },
];

const blank = {
  industry: "",
  industry_other: "",
  preferred_style: "",
  preferred_settings: "",
  wardrobe_notes: "",
  hair_description: "",
  skin_tone: "",
  appearance_other: "",
  additional_notes: "",
};

export default function Step2Brand() {
  const navigate = useNavigate();
  const [form, setForm] = useState(blank);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);

  useEffect(() => {
    api.get("/brand-profile").then((r) => {
      if (r.data.profile) {
        const p = r.data.profile;
        setForm({
          industry: p.industry || "",
          industry_other: p.industry_other || "",
          preferred_style: p.preferred_style || "",
          preferred_settings: p.preferred_settings || "",
          wardrobe_notes: p.wardrobe_notes || "",
          hair_description: p.hair_description || "",
          skin_tone: p.skin_tone || "",
          appearance_other: p.appearance_other || "",
          additional_notes: p.additional_notes || "",
        });
        setHasSaved(true);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async ({ andContinue }) => {
    setSaving(true);
    try {
      await api.post("/brand-profile", form);
      setHasSaved(true);
      toast.success("Brand profile saved");
      if (andContinue) navigate("/app/step3");
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="step2-loading">
        <Loader2 className="w-8 h-8 text-[#D4A853] animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto" data-testid="step2-page">
      <div className="flex items-center gap-3 mb-2">
        <span className="hairline" />
        <span className="eyebrow">Step 2 of 6</span>
      </div>
      <h1 className="font-display text-4xl md:text-5xl text-[#1A1F3D]">Step 2: Tell Us About Your Brand</h1>
      <p className="text-[#52525b] mt-3 text-lg max-w-3xl leading-relaxed">
        We'll use this to personalise your prompts so the AI creates images that match your brand, not someone else's.
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          save({ andContinue: true });
        }}
        className="mt-10 space-y-6"
      >
        {/* Industry */}
        <Section number="01" title="What's your industry or profession?" testid="section-industry">
          <select
            className="bsp-input"
            required
            value={form.industry}
            onChange={(e) => set("industry", e.target.value)}
            data-testid="field-industry"
          >
            <option value="" disabled>Choose your industry…</option>
            {INDUSTRIES.map((i) => (
              <option key={i} value={i}>{i}</option>
            ))}
          </select>
          {form.industry === "Other" && (
            <input
              className="bsp-input mt-3"
              required
              placeholder="Tell us your industry"
              value={form.industry_other}
              onChange={(e) => set("industry_other", e.target.value)}
              data-testid="field-industry-other"
            />
          )}
        </Section>

        {/* Style */}
        <Section number="02" title="What style best describes your brand?" testid="section-style">
          <div className="grid sm:grid-cols-2 gap-3">
            {STYLES.map((s) => {
              const active = form.preferred_style === s.value;
              return (
                <label
                  key={s.value}
                  className={`relative flex gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                    active ? "border-[#D4A853] bg-[#F5E6E0]/40 ring-1 ring-[#D4A853]" : "border-[#e5e7eb] bg-white hover:border-[#D4A853]/60"
                  }`}
                  data-testid={`style-${s.value.split(" ")[0].toLowerCase()}`}
                >
                  <input
                    type="radio"
                    name="style"
                    value={s.value}
                    checked={active}
                    onChange={() => set("preferred_style", s.value)}
                    className="mt-1 accent-[#D4A853]"
                    required
                  />
                  <div className="flex-1">
                    <div className="font-medium text-[#1A1F3D]">{s.value}</div>
                    <div className="text-sm text-[#52525b] mt-0.5">{s.desc}</div>
                  </div>
                  {active && <Check className="w-4 h-4 text-[#D4A853] absolute top-3 right-3" />}
                </label>
              );
            })}
          </div>
        </Section>

        {/* Settings */}
        <Section number="03" title="Describe your ideal photo settings" testid="section-settings">
          <textarea
            className="bsp-input"
            rows={3}
            placeholder="e.g. Modern office, outdoor cafe, home studio, city street, beach at sunset…"
            value={form.preferred_settings}
            onChange={(e) => set("preferred_settings", e.target.value)}
            data-testid="field-settings"
          />
        </Section>

        {/* Wardrobe */}
        <Section number="04" title="Describe your typical wardrobe / outfit" testid="section-wardrobe">
          <textarea
            className="bsp-input"
            rows={3}
            placeholder="e.g. Navy blazer with white shirt, smart casual, athleisure, bold colours…"
            value={form.wardrobe_notes}
            onChange={(e) => set("wardrobe_notes", e.target.value)}
            data-testid="field-wardrobe"
          />
        </Section>

        {/* Appearance */}
        <Section number="05" title="Describe your appearance" subtitle="Helps the AI match you." testid="section-appearance">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Hair">
              <input
                className="bsp-input"
                placeholder="e.g. Long dark brown hair, shoulder length"
                value={form.hair_description}
                onChange={(e) => set("hair_description", e.target.value)}
                data-testid="field-hair"
              />
            </Field>
            <Field label="Skin tone">
              <input
                className="bsp-input"
                placeholder="e.g. Light olive complexion"
                value={form.skin_tone}
                onChange={(e) => set("skin_tone", e.target.value)}
                data-testid="field-skin"
              />
            </Field>
          </div>
          <Field label="Any other details" className="mt-4">
            <textarea
              className="bsp-input"
              rows={3}
              placeholder="e.g. I wear glasses, I have freckles"
              value={form.appearance_other}
              onChange={(e) => set("appearance_other", e.target.value)}
              data-testid="field-appearance-other"
            />
          </Field>
        </Section>

        {/* Notes */}
        <Section number="06" title="Anything else the AI should know?" subtitle="Optional." testid="section-notes">
          <textarea
            className="bsp-input"
            rows={3}
            placeholder="Recurring jewellery, signature colours, body language preferences…"
            value={form.additional_notes}
            onChange={(e) => set("additional_notes", e.target.value)}
            data-testid="field-notes"
          />
        </Section>

        {/* Actions */}
        <div className="bsp-card p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-8" data-testid="step2-actions">
          <div className="flex items-center gap-3">
            {hasSaved ? (
              <>
                <div className="w-9 h-9 rounded-full bg-[#D4A853] flex items-center justify-center">
                  <Check className="w-4 h-4 text-[#1A1F3D]" strokeWidth={2.4} />
                </div>
                <div>
                  <div className="font-display text-lg text-[#1A1F3D]">Step 2 saved</div>
                  <div className="text-sm text-[#52525b]">You can edit and re-save anytime.</div>
                </div>
              </>
            ) : (
              <div>
                <div className="font-display text-lg text-[#1A1F3D]">Save your answers</div>
                <div className="text-sm text-[#52525b]">Then we'll personalise your prompts.</div>
              </div>
            )}
          </div>
          <div className="flex gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => save({ andContinue: false })}
              disabled={saving}
              className="btn-ghost inline-flex"
              data-testid="save-btn"
            >
              <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn-primary"
              data-testid="save-continue-btn"
            >
              {saving ? "Saving…" : "Save & Continue to Step 3"} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </form>

      <div className="mt-8 text-sm text-[#52525b]">
        Need to go back? <Link to="/app/step1" className="gold-link">Step 1: Reference Photos</Link>
      </div>
    </div>
  );
}

const Section = ({ number, title, subtitle, children, testid }) => (
  <section className="bsp-card p-6 md:p-8" data-testid={testid}>
    <div className="flex items-baseline gap-3 mb-5">
      <span className="font-display text-2xl text-[#D4A853]">{number}</span>
      <div>
        <h2 className="font-display text-xl md:text-2xl text-[#1A1F3D]">{title}</h2>
        {subtitle && <p className="text-sm text-[#52525b] mt-0.5">{subtitle}</p>}
      </div>
    </div>
    {children}
  </section>
);

const Field = ({ label, className = "", children }) => (
  <div className={className}>
    <label className="text-sm font-medium text-[#1A1F3D]">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);
